import { IRest } from './rest.interface';
import { Vehicle } from './vehicle';
import { Route } from './path';
import { Team } from './team';
import { Member } from './user';
import { Contract } from './contract';
import {
  InspectionSignalingModeEnum,
  InspectionSignalingStatusEnum,
  InspectionStatusEnum,
  SignalingConditionEnum,
} from 'src/enumerators';

export class InspectionAddress implements IRest {
  className = 'Endereço da sinalização de vistoria';

  city: string;
  house_number: string;
  road: string;
  suburb: string;
  postcode: string;

  constructor(data?: any) {
    if (data) this.Deserialize(data);
  }

  Serialize() {
    return {
      city: this.city,
      house_number: this.house_number,
      road: this.road,
      suburb: this.suburb,
      postcode: this.postcode,
    };
  }

  Deserialize(data: any) {
    if (data) {
      this.city = data['city'] || '';
      this.house_number = data['house_number'] || '';
      this.road = data['road'] || '';
      this.suburb = data['suburb'] || '';
      this.postcode = data['postcode'] || '';
    }
    return this;
  }

  /** Linha legível para a tabela e o popup do mapa. */
  get label(): string {
    return [this.road, this.house_number, this.suburb, this.city]
      .filter((part) => !!part)
      .join(', ');
  }
}

export class InspectionSignFile implements IRest {
  className = 'Arquivo da sinalização de vistoria';

  id: string;
  fileId: string;
  inspectionSignalingId: string;
  url: string;
  processed: boolean;
  cropped: boolean;
  createdAt: Date;
  updatedAt: Date;

  constructor(data?: any) {
    if (data) this.Deserialize(data);
  }

  Serialize() {
    return {
      id: this.id,
      fileId: this.fileId,
      inspectionSignalingId: this.inspectionSignalingId,
      url: this.url,
      processed: this.processed,
      cropped: this.cropped,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  Deserialize(data: any) {
    this.id = data['id'] || undefined;
    this.fileId = data['fileId'] || undefined;
    this.inspectionSignalingId = data['inspectionSignalingId'] || undefined;
    this.url = data['url'] || undefined;
    this.processed = data['processed'] || false;
    this.cropped = data['cropped'] || false;
    this.createdAt = data['createdAt'] || undefined;
    this.updatedAt = data['updatedAt'] || undefined;
    return this;
  }
}

export class InspectionSign implements IRest {
  className = 'Sinalização de vistoria';

  id: string;
  inspectionId: string;
  latitude: number;
  longitude: number;
  address: InspectionAddress;
  status: InspectionSignalingStatusEnum;
  mode: InspectionSignalingModeEnum;
  reidentificationCompleted: boolean;
  reidentificationRequestedAt: Date | undefined;
  createdAt: Date;
  updatedAt: Date;

  /*
   * `conditions` e `contentText` NÃO são colunas no Hellius.
   *
   * O backend guarda os dois dentro da coluna JSON `data` de
   * `InspectionSignaling` (o backend-der-df usa colunas separadas — divergência
   * deliberada, mantida). Aqui eles são expostos planos para a tela não precisar
   * saber disso, e `Serialize` os devolve para dentro de `data`.
   */
  conditions: Array<SignalingConditionEnum>;
  contentText: string;

  /*
   * Código da placa no catálogo CONTRAN ("R-1", "A-32b"), detectado pelo HORUS.
   *
   * Vem de `data.contranCode`, gravado pelo cron a partir do `classType` da fila
   * `/images-contran`. Não é o mesmo que o `verticalCode` de uma placa
   * catalogada: este é o que o modelo reconheceu, ainda sem revisão humana.
   */
  contranCode: string;

  /** Preenchido pela tela, não vem da API. */
  files: Array<InspectionSignFile> = [];
  duplicated = false;

  /*
   * `data` cru como veio da API, para round-trip.
   *
   * `PUT /inspections/:id/signalings/:id` SUBSTITUI a coluna `data` inteira pelo
   * que o cliente manda (`InspectionSignalingDto` copia `json.data` e o
   * repositório faz `update(data)`). Se o Serialize emitisse apenas as chaves que
   * este model conhece, uma troca de status pela tela apagaria as demais — hoje
   * `detectionClass`, gravada pelo cron, e o que o app de campo vier a gravar.
   *
   * Guardar o original e espalhá-lo no Serialize mantém o desconhecido intacto.
   */
  private rawData: Record<string, any> = {};

  constructor(data?: any) {
    if (data) this.Deserialize(data);
  }

  Serialize() {
    return {
      id: this.id,
      inspectionId: this.inspectionId,
      latitude: this.latitude,
      longitude: this.longitude,
      address: this.address?.Serialize(),
      status: this.status,
      mode: this.mode,
      reidentificationCompleted: this.reidentificationCompleted,
      // Espalha o `data` original primeiro; os campos conhecidos sobrescrevem.
      data: {
        ...this.rawData,
        conditions: this.conditions,
        contentText: this.contentText,
        contranCode: this.contranCode,
      },
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  Deserialize(data: any) {
    this.id = data['id'] || undefined;
    this.inspectionId = data['inspectionId'] || undefined;
    this.latitude = data['latitude'] ?? undefined;
    this.longitude = data['longitude'] ?? undefined;
    this.address = new InspectionAddress(data['address']);
    this.status = data['status'] || undefined;
    this.mode = data['mode'] || undefined;
    this.reidentificationCompleted = data['reidentificationCompleted'] || false;
    this.reidentificationRequestedAt = data['reidentificationRequestedAt'] || undefined;
    this.createdAt = data['createdAt'] || undefined;
    this.updatedAt = data['updatedAt'] || undefined;

    // Aceita os dois formatos: aninhado em `data` (o do Hellius) e plano (como
    // vem do backend-der-df). Sem o fallback, a mesma tela apontada para um dos
    // dois backends mostraria as condições vazias sem erro nenhum.
    const nested = data['data'] && typeof data['data'] === 'object' && !Array.isArray(data['data'])
      ? data['data']
      : {};
    this.rawData = nested;
    this.conditions = InspectionSign.normalizeConditions(nested['conditions'] ?? data['conditions']);
    this.contentText = nested['contentText'] ?? data['contentText'] ?? '';
    this.contranCode = nested['contranCode'] ?? data['contranCode'] ?? '';

    this.duplicated = false;
    return this;
  }

  /*
   * Normaliza a caixa das condições para MAIÚSCULAS.
   *
   * Verificado em produção (2026-08-20): `InspectionSignaling.conditions` e
   * `TrafficSign.conditions` guardam MAIÚSCULAS (NORMAL, GRAFFITIED, OXIDIZED,
   * PEELING…), e o contrato do `/traffic-sign-reid/images` também usa
   * maiúsculas. Mas o `/traffic-sign-inspection/results` devolve MINÚSCULAS
   * ('normal', 'peeling', 'stickered') — confirmado enviando placas reais ao
   * serviço.
   *
   * Hoje isso não vaza para a tela porque o cron descarta o `conditions` do
   * HORUS. Se alguém passar a gravá-lo, o valor chega em minúsculas e
   * `| enumText: 'SignalingCondition'` não acha a chave — o rótulo simplesmente
   * não aparece, sem erro. Normalizar aqui custa uma linha e fecha o caso para
   * qualquer uma das origens.
   */
  private static normalizeConditions(value: any): Array<SignalingConditionEnum> {
    if (!Array.isArray(value)) return [];

    return value
      .filter((item) => typeof item === 'string' && item.length > 0)
      .map((item) => String(item).toUpperCase() as SignalingConditionEnum);
  }

  /** `true` enquanto o HORUS não devolveu a reidentificação desta sinalização. */
  get awaitingReidentification(): boolean {
    return !!this.reidentificationRequestedAt && !this.reidentificationCompleted;
  }
}

export class Inspection implements IRest {
  className = 'Vistoria';

  id: string;
  workspaceId: string;
  vehicleId: string;
  teamId: string;
  routeId: string;
  contractId: string;
  memberId: string;
  name: string | undefined;
  observations: string | undefined;
  address: InspectionAddress;
  startDate: Date;
  endDate: Date;
  completedAt: Date | undefined;
  status: InspectionStatusEnum;
  signalingsQuantity: number;
  automaticSignalingsQuantity: number;
  manualSignalingsQuantity: number;
  pendingQuantity: number;
  createdAt: Date;
  updatedAt: Date;

  /** Relações resolvidas pela tela a partir dos *Id acima. */
  vehicle: Vehicle;
  route: Route;
  team: Team;
  member: Member;
  contract: Contract;
  signs: InspectionSign[] = [];

  constructor(data?: any) {
    if (data) this.Deserialize(data);
  }

  Serialize() {
    return {
      id: this.id,
      workspaceId: this.workspaceId,
      contractId: this.contractId,
      memberId: this.memberId,
      vehicleId: this.vehicleId,
      teamId: this.teamId,
      routeId: this.routeId,
      name: this.name,
      observations: this.observations,
      startDate: this.startDate,
      endDate: this.endDate,
      status: this.status,
      signalingsQuantity: this.signalingsQuantity,
      pendingQuantity: this.pendingQuantity,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  Deserialize(data: any) {
    this.id = data['id'] || undefined;
    this.workspaceId = data['workspaceId'] || undefined;
    this.memberId = data['memberId'] || undefined;
    this.contractId = data['contractId'] || undefined;
    this.vehicleId = data['vehicleId'] || undefined;
    this.teamId = data['teamId'] || undefined;
    this.routeId = data['routeId'] || undefined;
    this.name = data['name'] || undefined;
    this.observations = data['observations'] || undefined;
    this.address = new InspectionAddress(data['address']);
    this.startDate = data['startDate'] || undefined;
    this.endDate = data['endDate'] || undefined;
    this.completedAt = data['completedAt'] || undefined;
    this.status = data['status'] || undefined;
    this.createdAt = data['createdAt'] || undefined;
    this.updatedAt = data['updatedAt'] || undefined;
    this.signalingsQuantity = data['signalingsQuantity'] || 0;
    this.automaticSignalingsQuantity = data['automaticSignalingsQuantity'] || 0;
    this.manualSignalingsQuantity = data['manualSignalingsQuantity'] || 0;
    this.pendingQuantity = data['pendingQuantity'] || 0;

    // O `show` do backend devolve a rota aninhada; o `index` não (resolve-se
    // pelo `routeId` contra a lista de rotas já carregada na tela).
    if (data['route']) this.route = new Route(data['route']);

    return this;
  }

  /** Quantas sinalizações já saíram de "aguardando". */
  get reviewedQuantity(): number {
    return Math.max(0, (this.signalingsQuantity || 0) - (this.pendingQuantity || 0));
  }
}

export class Reidentification implements IRest {
  className = 'Reidentificação de sinalização de vistoria';

  id: string;
  workspaceId: string;
  inspectionSignalingId: string;
  reidentifiedInspectionSignalingId: string;
  confidence: number;
  distance: number;
  createdAt: Date;
  updatedAt: Date;

  constructor(data?: any) {
    if (data) this.Deserialize(data);
  }

  Serialize() {
    return {
      id: this.id,
      workspaceId: this.workspaceId,
      inspectionSignalingId: this.inspectionSignalingId,
      reidentifiedInspectionSignalingId: this.reidentifiedInspectionSignalingId,
      confidence: this.confidence,
      distance: this.distance,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  Deserialize(data: any) {
    this.id = data['id'] || undefined;
    this.workspaceId = data['workspaceId'] || undefined;
    this.inspectionSignalingId = data['inspectionSignalingId'] || undefined;
    this.reidentifiedInspectionSignalingId = data['reidentifiedInspectionSignalingId'] || undefined;
    this.confidence = data['confidence'] ?? undefined;
    this.distance = data['distance'] ?? undefined;
    this.createdAt = data['createdAt'] || undefined;
    this.updatedAt = data['updatedAt'] || undefined;
    return this;
  }
}
